import React, { useCallback, useEffect, useState } from "react";

import moment from "moment";

import { getUserActionedArticleOrNews, saveArticleOrNews } from "services";

import { formatDate, goTo, truncateString, useCancelAsyncOnUnmount, useGetFullNews, useInView, useNavigate, useTranslation, useUserContext } from "utils";

import { Bookmark, Card, LazyLoadImage, Link, Skeleton, TagRecommended, TagText, Typography } from "components";

export const NewsCard = ({ link, small }: { link: string; small?: boolean }) => {
  const { t } = useTranslation("news");

  const { user } = useUserContext();
  const [userActions, setUserActions] = useState({
    allowed: false,
    saved: false,
    set: false,
  });
  const signal = useCancelAsyncOnUnmount()();
  const navigate = useNavigate();
  const { news } = useGetFullNews({ link: link || "unkown" });

  const { ref, inView } = useInView({
    threshold: 0,
    triggerOnce: true,
    trackVisibility: true,
    delay: 100,
  });

  const loadUserActionedArticleOrNews = useCallback(() => {
    getUserActionedArticleOrNews({
      link: link,
      signal,
    }).then((res) => {
      setUserActions({
        allowed: !!res?.allowed,
        saved: !!res?.saved,
        set: true,
      });
    });
  }, [link, signal]);

  const toggleSave = (link: string) => {
    saveArticleOrNews({
      link: link,
      signal,
    }).then(loadUserActionedArticleOrNews);
  };

  useEffect(() => {
    if (link && inView && user) {
      loadUserActionedArticleOrNews();
    }
  }, [link, user, inView]); // eslint-disable-line

  return (
    <div ref={ref}>
      {news?.title ? (
        <Card
          className={small ? "article-card small" : "article-card"}
          onClick={(e) => goTo(e, navigate, user ? `/news/${news.link}` : `/news/${news.link}`)}
          mixpanelText={`news-card ${news.title}`}
        >
          <div className="image">
            <LazyLoadImage alt={news.altImageText} src={news.images.s} />
          </div>
          <div className="content">
            <div className="tags">
              {news.recommended && <TagRecommended text={t("newsCard.recommended")} />}
              <TagText text={news.methods.length > 0 ? news.methods[0].method : news.tags.length ? news.tags[0].tag : ""} />
              {userActions.allowed && user && (
                <span onClick={(e) => e.stopPropagation()}>
                  {/* @ts-expect-error need to convert to tsx */}
                  <Bookmark colour="#202020" checked={userActions.saved} onChange={() => toggleSave(link)} initialValue={userActions.saved} />
                </span>
              )}
            </div>
            <div className="headline">
              <Link
                to={user ? `/news/${news.link}` : `/news/${news.link}`}
                onClick={(e: React.MouseEvent<HTMLElement, MouseEvent>) => e.stopPropagation()}
                mixpanelText={news.title}
              >
                <Typography variant="h3">{truncateString(news.title, 72)}</Typography>
              </Link>
            </div>
            <div className="date-and-reading-time">
              <div className="date">
                {news.updatedAt ? formatDate(news.updatedAt, "DD.MM.YYYY", moment.ISO_8601) : formatDate(news.createdAt, "DD.MM.YYYY", moment.ISO_8601)}
              </div>
              <div className="reading-time margin-left-50">{t("newsCard.readingTime", { time: news.readingTime })}</div>
            </div>
          </div>
        </Card>
      ) : (
        <Card className={small ? "article-card small" : "article-card"}>
          {small ? (
            <div className="content">
              <Skeleton height="192px" type="paragraph" rows={3} />
            </div>
          ) : (
            <>
              <Skeleton type="block" height="192px" />
              <div className="content">
                <Skeleton type="title" />
                <Skeleton type="paragraph" rows={3} />
              </div>
            </>
          )}
        </Card>
      )}
    </div>
  );
};

export default NewsCard;
